import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { 
  sanitizeText, 
  sanitizeAddress, 
  validateEmail, 
  validatePhone, 
  validateTheoryTestStatus,
  validateLicenceNumber,
  maskSensitiveData
} from '@/lib/security';

export interface Profile {
  id: string;
  user_id: string;
  role: 'student' | 'instructor';
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  avatar_url?: string;
  licence_number?: string;
  driving_goals?: string;
  theory_test_status: 'not_started' | 'booked' | 'passed';
  theory_test_date?: string;
  driving_test_date?: string;
  driving_test_centre?: string;
  address?: string;
  postcode?: string;
  driving_school_name?: string;
  bio?: string;
  car_image_url?: string;
  car_image_updated_at?: string;
  login_status: string;
  status: 'active' | 'deleted' | 'suspended';
  created_at: string;
  updated_at: string;
}

export const useProfile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      setHasAttemptedLoad(false);
      return;
    }

    // Prevent re-fetching if already attempted for this user
    if (hasAttemptedLoad) {
      return;
    }

    let isMounted = true;
    let attempts = 0;
    const maxAttempts = 3; // Reduced retry attempts
    const baseDelay = 500;

    const fetchProfile = async () => {
      if (!isMounted) return;

      try {
        if (attempts === 0) setLoading(true);

        console.log('[Profile] PRIMARY: Fetching profile for user:', user.id);
        
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!isMounted) return;

        // PRIMARY: Handle profile fetch errors - DO NOT call signOut here
        // Let AuthProvider handle all logout logic to prevent cascading events
        if (error) {
          console.error('[Profile] Fetch error:', error.code, error.message);
          
          // Check for auth-related errors
          const errorCode = error.code;
          const errorMessage = error.message?.toLowerCase() || '';
          
          if (
            errorCode === 'PGRST301' || // Auth required
            errorCode === 'PGRST116' || // JWT expired
            errorCode === '401' ||
            errorCode === '403' ||
            errorMessage.includes('jwt') ||
            errorMessage.includes('expired') ||
            errorMessage.includes('unauthorized')
          ) {
            console.warn('[Profile] Auth error detected - session likely expired. AuthProvider will handle logout.');
            setError('Session expired. Please log in again.');
          } else {
            // For other errors, just set error state
            setError(error.message);
          }
          
          setLoading(false);
          setHasAttemptedLoad(true);
          return;
        }

        if (data) {
          console.log('[Profile] ✓ Profile loaded successfully');
          setProfile(data as Profile);
          setLoading(false);
          setHasAttemptedLoad(true);
          return;
        }

        // Profile doesn't exist yet (null data, no error) - retry with exponential backoff
        if (!data && !error && attempts < maxAttempts) {
          attempts += 1;
          console.log(`Profile not found, retrying... attempt ${attempts}/${maxAttempts}`);
          const delay = baseDelay * Math.pow(2, attempts - 1);
          setTimeout(fetchProfile, delay);
          return;
        }

        // Give up after retries
        console.log('Profile not found after max attempts, giving up');
        setProfile(null);
        setLoading(false);
        setHasAttemptedLoad(true);
      } catch (err) {
        if (!isMounted) return;
        console.error('Profile fetch exception:', err);
        setError('Failed to fetch profile');
        setLoading(false);
        setHasAttemptedLoad(true);
      }
    };

    fetchProfile();

    return () => {
      isMounted = false;
    };
  }, [user, hasAttemptedLoad]);

  // Real-time subscription to profile updates
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`profile-updates-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('[Profile] Real-time update received:', payload.new);
          if (payload.new) {
            setProfile(payload.new as Profile);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const updateProfile = async (updates: Partial<Profile>) => {
    console.log('🟡 ===== UPDATE PROFILE FUNCTION CALLED =====');
    console.log('🟡 Updates received:', JSON.stringify(updates, null, 2));
    console.log('🟡 User exists:', !!user);
    console.log('🟡 Profile exists:', !!profile);
    
    if (!user || !profile) {
      console.error('❌ updateProfile called but user or profile is missing:', { user: !!user, profile: !!profile });
      return;
    }

    console.log('🔄 updateProfile called with:', updates);
    console.log('📋 Current user:', user.id);
    console.log('📋 Current profile:', profile);

    try {
      // Prevent role updates through regular profile updates for security
      const { role, ...rawUpdates } = updates;
      
      console.log('🔍 Processing updates (after removing role):', rawUpdates);
      
      // Validate and sanitize all inputs
      const safeUpdates: Partial<Profile> = {};
      
      // Sanitize text fields
      if (rawUpdates.first_name !== undefined) {
        safeUpdates.first_name = sanitizeText(rawUpdates.first_name, 50);
      }
      if (rawUpdates.last_name !== undefined) {
        safeUpdates.last_name = sanitizeText(rawUpdates.last_name, 50);
      }
      if (rawUpdates.driving_goals !== undefined && rawUpdates.driving_goals !== null) {
        safeUpdates.driving_goals = sanitizeText(rawUpdates.driving_goals, 500);
      }
      if (rawUpdates.licence_number !== undefined && rawUpdates.licence_number !== null) {
        // Enhanced validation for driving licence numbers
        if (!validateLicenceNumber(rawUpdates.licence_number)) {
          throw new Error('Invalid driving licence number format');
        }
        safeUpdates.licence_number = sanitizeText(rawUpdates.licence_number, 20);
      }
      if (rawUpdates.address !== undefined && rawUpdates.address !== null) {
        console.log('🔍 Processing address:', rawUpdates.address);
        try {
          safeUpdates.address = sanitizeAddress(rawUpdates.address);
          console.log('✅ Address sanitized successfully:', safeUpdates.address);
        } catch (error) {
          console.error('❌ Address sanitization failed:', error);
          throw new Error(`Address validation failed: ${error instanceof Error ? error.message : 'Invalid address'}`);
        }
      }
      if (rawUpdates.postcode !== undefined && rawUpdates.postcode !== null) {
        console.log('🔍 Processing postcode:', rawUpdates.postcode);
        try {
          safeUpdates.postcode = sanitizeText(rawUpdates.postcode, 10);
          console.log('✅ Postcode sanitized successfully:', safeUpdates.postcode);
        } catch (error) {
          console.error('❌ Postcode sanitization failed:', error);
          throw new Error(`Postcode validation failed: ${error instanceof Error ? error.message : 'Invalid postcode'}`);
        }
      }
      if (rawUpdates.driving_school_name !== undefined && rawUpdates.driving_school_name !== null) {
        safeUpdates.driving_school_name = sanitizeText(rawUpdates.driving_school_name, 100);
      }
      if (rawUpdates.bio !== undefined && rawUpdates.bio !== null) {
        safeUpdates.bio = sanitizeText(rawUpdates.bio, 300);
      }
      
      // Pass through car image fields (already validated/uploaded in component)
      if (rawUpdates.car_image_url !== undefined) {
        safeUpdates.car_image_url = rawUpdates.car_image_url;
      }
      if (rawUpdates.car_image_updated_at !== undefined) {
        safeUpdates.car_image_updated_at = rawUpdates.car_image_updated_at;
      }
      
      // Validate email
      if (rawUpdates.email !== undefined) {
        if (!validateEmail(rawUpdates.email)) {
          throw new Error('Invalid email format');
        }
        safeUpdates.email = rawUpdates.email;
      }
      
      if (rawUpdates.phone !== undefined && rawUpdates.phone !== null) {
        console.log('🔍 Processing phone:', rawUpdates.phone);
        try {
          if (!validatePhone(rawUpdates.phone)) {
            throw new Error('Invalid phone number format');
          }
          safeUpdates.phone = rawUpdates.phone;
          console.log('✅ Phone validated successfully:', safeUpdates.phone);
        } catch (error) {
          console.error('❌ Phone validation failed:', error);
          throw new Error(`Phone validation failed: ${error instanceof Error ? error.message : 'Invalid phone number'}`);
        }
      }
      
      // Validate theory test status
      if (rawUpdates.theory_test_status !== undefined) {
        if (!validateTheoryTestStatus(rawUpdates.theory_test_status)) {
          throw new Error('Invalid theory test status');
        }
        safeUpdates.theory_test_status = rawUpdates.theory_test_status;
      }
      
      // Validate driving test date
      if (rawUpdates.driving_test_date !== undefined && rawUpdates.driving_test_date !== null) {
        const testDate = new Date(rawUpdates.driving_test_date);
        if (isNaN(testDate.getTime()) || testDate < new Date()) {
          throw new Error('Driving test date must be in the future');
        }
        safeUpdates.driving_test_date = rawUpdates.driving_test_date;
      }
      
      // Avatar URL validation (basic)
      if (rawUpdates.avatar_url !== undefined && rawUpdates.avatar_url !== null) {
        try {
          new URL(rawUpdates.avatar_url);
          safeUpdates.avatar_url = rawUpdates.avatar_url;
        } catch {
          throw new Error('Invalid avatar URL format');
        }
      }
      
      console.log('📝 Final safeUpdates to send to database:', safeUpdates);
      
      if (Object.keys(safeUpdates).length === 0) {
        console.warn('⚠️ No valid updates to process');
        return;
      }
      
      console.log('📝 Updating profile with:', safeUpdates);
      
      const { error } = await supabase
        .from('profiles')
        .update(safeUpdates)
        .eq('user_id', user.id);

      if (error) {
        console.error('❌ Database update error:', error);
        throw error;
      }

      console.log('✅ Database update successful');

      // Update local state
      setProfile(prev => prev ? { ...prev, ...safeUpdates } : null);
      
      console.log('✅ Profile updated successfully, real-time updates will propagate automatically');
      
      // If this is a student profile update and we have address/phone/contact details,
      // ensure the student is properly linked to instructors in the system
      if (profile.role === 'student' && (safeUpdates.phone || safeUpdates.address || safeUpdates.postcode)) {
        console.log('🔗 Student contact details updated, ensuring instructor links are active');
        try {
          // Check if student has any instructor relationships that need to be activated
          const { data: existingLinks, error: linkError } = await supabase
            .from('instructor_students')
            .select('*')
            .eq('student_id', user.id);
          
          if (linkError) {
            console.error('Error checking instructor links:', linkError);
          } else {
            console.log('🔍 Found instructor links for student:', existingLinks);
            
            // Update any inactive links to active status
            if (existingLinks && existingLinks.length > 0) {
              const { error: updateLinkError } = await supabase
                .from('instructor_students')
                .update({ status: 'active' })
                .eq('student_id', user.id)
                .neq('status', 'active');
                
              if (updateLinkError) {
                console.error('Error updating instructor link status:', updateLinkError);
              } else {
                console.log('✅ Instructor links activated successfully');
              }
            }
          }
        } catch (linkErr) {
          console.error('❌ Error ensuring instructor links:', linkErr);
          // Don't throw here as profile update was successful
        }
      }
    } catch (err) {
      console.error('❌ updateProfile error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update profile';
      console.error('❌ Error message:', errorMessage);
      setError(errorMessage);
      throw err;
    }
  };

  return { profile, loading, error, updateProfile };
};